import React, { useState } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import RevenueKPICard from './components/RevenueKPICard';
import RevenueBreakdownChart from './components/RevenueBreakdownChart';
import MerchantLeaderboard from './components/MerchantLeaderboard';
import FinancialDataTable from './components/FinancialDataTable';
import ScenarioModeling from './components/ScenarioModeling';
import CostOptimizationPanel from './components/CostOptimizationPanel';

const FinancialPerformance = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('Q4 2025');

  const kpiData = [
  {
    title: 'Total Revenue',
    value: '$2.45M',
    change: '+18.2%',
    changeType: 'positive',
    icon: 'DollarSign',
    iconColor: 'var(--color-success)',
    forecast: '$2.8M projected',
    subtitle: 'All revenue streams'
  },
  {
    title: 'Profit Margin',
    value: '30.2%',
    change: '+2.4pp',
    changeType: 'positive',
    icon: 'TrendingUp',
    iconColor: 'var(--color-primary)',
    forecast: '32% target',
    subtitle: 'Net margin after costs'
  },
  {
    title: 'Cost Per Transaction',
    value: '$0.42',
    change: '-8.5%',
    changeType: 'positive',
    icon: 'Receipt',
    iconColor: 'var(--color-accent)',
    forecast: '$0.38 target',
    subtitle: 'Blended average'
  },
  {
    title: 'Merchant Growth',
    value: '+142',
    change: '+24.1%',
    changeType: 'positive',
    icon: 'Store',
    iconColor: 'var(--color-secondary)',
    forecast: '+180 projected',
    subtitle: 'New merchants this quarter'
  }];


  const revenueChartData = [
  { month: 'Oct', swapFees: 285, merchantFees: 198, subscriptions: 87, margin: 28.5 },
  { month: 'Nov', swapFees: 312, merchantFees: 215, subscriptions: 92, margin: 29.2 },
  { month: 'Dec', swapFees: 348, merchantFees: 238, subscriptions: 98, margin: 30.8 },
  { month: 'Jan', swapFees: 365, merchantFees: 252, subscriptions: 105, margin: 31.2 },
  { month: 'Feb', swapFees: 392, merchantFees: 268, subscriptions: 112, margin: 31.8 },
  { month: 'Mar', swapFees: 418, merchantFees: 285, subscriptions: 118, margin: 32.4 }];


  const topMerchants = [
  {
    id: 1,
    rank: 1,
    name: 'TechMart Global',
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_122d4ecb4-1767046520815.png",
    logoAlt: 'TechMart Global logo featuring blue and white geometric design with shopping cart icon',
    transactions: 45823,
    fees: 128450,
    growth: 32.5
  },
  {
    id: 2,
    rank: 2,
    name: 'CryptoExchange Pro',
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1ce404267-1768731695594.png",
    logoAlt: 'CryptoExchange Pro logo with golden Bitcoin symbol on dark background',
    transactions: 38912,
    fees: 98320,
    growth: 28.3
  },
  {
    id: 3,
    rank: 3,
    name: 'Digital Goods Hub',
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_114157ab4-1768731692297.png",
    logoAlt: 'Digital Goods Hub logo showing purple gradient with download icon',
    transactions: 32456,
    fees: 76890,
    growth: 18.7
  },
  {
    id: 4,
    rank: 4,
    name: 'Gaming Marketplace',
    logo: "https://images.unsplash.com/photo-1632222294783-ed61bd9e6ed7",
    logoAlt: 'Gaming Marketplace logo with neon green controller icon on black background',
    transactions: 28934,
    fees: 65420,
    growth: 15.2
  },
  {
    id: 5,
    rank: 5,
    name: 'Fashion Forward',
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_12e55f7e7-1767079295330.png",
    logoAlt: 'Fashion Forward logo featuring elegant script text with clothing hanger icon',
    transactions: 24567,
    fees: 58230,
    growth: -3.4
  }];


  const financialTableData = [
  {
    id: 1,
    category: 'Bitcoin Rail',
    subcategory: 'BTC transactions',
    icon: 'Bitcoin',
    color: '#F7931A',
    revenue: 485000,
    costs: 145500,
    margin: 70.0,
    growth: 42.3,
    details: {
      volume: 12450,
      avgValue: 389,
      costPerTxn: 11.68
    }
  },
  {
    id: 2,
    category: 'Ethereum Rail',
    subcategory: 'ETH & ERC-20 tokens',
    icon: 'Hexagon',
    color: '#627EEA',
    revenue: 398000,
    costs: 119400,
    margin: 70.0,
    growth: 38.7,
    details: {
      volume: 18920,
      avgValue: 210,
      costPerTxn: 6.31
    }
  },
  {
    id: 3,
    category: 'Stablecoin Rail',
    subcategory: 'USDC, USDT, DAI',
    icon: 'Coins',
    color: '#26A17B',
    revenue: 892000,
    costs: 267600,
    margin: 70.0,
    growth: 56.2,
    details: {
      volume: 45680,
      avgValue: 195,
      costPerTxn: 5.86
    }
  },
  {
    id: 4,
    category: 'Credit Card Rail',
    subcategory: 'Visa, Mastercard, Amex',
    icon: 'CreditCard',
    color: '#0066CC',
    revenue: 675000,
    costs: 1182500,
    margin: -75.2,
    growth: 12.4,
    details: {
      volume: 89340,
      avgValue: 76,
      costPerTxn: 13.24
    }
  }];


  const optimizationRecommendations = [
  {
    id: 1,
    title: 'Optimize Credit Card Processing Costs',
    description: 'Switch to direct processor integration for high-volume merchants to reduce interchange fees by 0.3-0.5%. Current blended rate is 2.9% + $0.30.',
    icon: 'CreditCard',
    color: 'var(--color-error)',
    impact: 'high',
    savings: 18500,
    timeframe: '2-3 months',
    effort: 'Medium'
  },
  {
    id: 2,
    title: 'Implement Dynamic Swap Fee Pricing',
    description: 'Introduce tiered pricing based on transaction volume and volatility. High-volume users get lower fees, increasing retention and volume.',
    icon: 'TrendingUp',
    color: 'var(--color-success)',
    impact: 'high',
    savings: 24000,
    timeframe: '1 month',
    effort: 'Low'
  },
  {
    id: 3,
    title: 'Reduce Blockchain Gas Costs',
    description: 'Batch small transactions and implement Layer 2 solutions for Ethereum operations. Current gas costs are 30% above optimal.',
    icon: 'Zap',
    color: 'var(--color-warning)',
    impact: 'medium',
    savings: 12300,
    timeframe: '3-4 months',
    effort: 'High'
  },
  {
    id: 4,
    title: 'Automate Compliance Processes',
    description: 'Deploy AI-powered KYC/AML screening to reduce manual review costs by 60%. Current cost per verification is $8.50.',
    icon: 'Shield',
    color: 'var(--color-primary)',
    impact: 'medium',
    savings: 9800,
    timeframe: '2 months',
    effort: 'Medium'
  }];


  return (
    <MainLayout showControls={true}>
      <div className="max-w-[1400px] mx-auto">
        <div className="mb-6 md:mb-8">
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-2">
            Financial Performance
          </h1>
          <p className="text-sm md:text-base text-muted-foreground">
            Comprehensive revenue analytics and cost optimization insights across all payment rails
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
          {kpiData?.map((kpi, index) =>
          <RevenueKPICard key={index} {...kpi} />
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-6 mb-6 md:mb-8">
          <div className="lg:col-span-9">
            <RevenueBreakdownChart data={revenueChartData} period={selectedPeriod} />
          </div>
          <div className="lg:col-span-3">
            <MerchantLeaderboard merchants={topMerchants} />
          </div>
        </div>

        <div className="mb-6 md:mb-8">
          <FinancialDataTable
            data={financialTableData}
            title="Revenue by Payment Rail"
            subtitle="Detailed breakdown with margin analysis and growth metrics" />

        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 mb-6 md:mb-8">
          <ScenarioModeling />
          <CostOptimizationPanel recommendations={optimizationRecommendations} />
        </div>
      </div>
    </MainLayout>);

};

export default FinancialPerformance;